import { useLocation } from "react-router-dom";
import { apTabIcon } from "@/accounts-payable/ap-tab-icon";
import { useGetReviewQueue } from "@/accounts-payable/query";
import { useApTabs } from "@/accounts-payable/use-ap-tabs";
import { useForwardingEmptyState } from "@/accounts-payable/use-forwarding-empty-state";
import {
  AP_TAB,
  AP_TAB_LABEL,
  apActiveTab,
  apListPath,
  flattenPages,
} from "@/accounts-payable/utils";
import { getProductCapabilities } from "@/business/product-capabilities";
import { useGetBusiness } from "@/business/query";
import { useBusiness } from "@/core/business";
import { useBusinessProducts } from "@/core/business/use-business-products";
import { FEATURE_FLAG_NAMES } from "@/core/feature-flags/feature-flag-name";
import { useFeatureFlag } from "@/core/feature-flags/use-feature-flag";
import {
  getPayBreakdownPath,
  PAY_BREAKDOWN_PATH,
} from "@/payroll/pay-breakdown/routes";
import { useCanViewPayrollRoster } from "@/payroll/roster/permissions";
import {
  payrollSettingsAccessPolicy,
  type PayrollSettingsAccessPolicy,
} from "@/payroll/settings/access-policy";
import { useWorkbookAccessEditorAccess } from "@/payroll/workbook-access/permissions";
import { useJobCostingNav } from "@/profitability/use-job-costing-nav";
import { useUserManagementAccess } from "@/user/management-access";
import { useGetMyUser } from "@/user/query";
import { isApOnly, isTech } from "@/user/utils";
import {
  isPathUnder,
  foldNavPanels,
  NAV_ITEM_DEFS,
  NAV_ROUTES,
  type NavItemDef,
  type NavVisibility,
  type SidebarSection,
} from "./nav-items";

export interface NavItem extends NavItemDef {
  isActive: boolean;
  to: string;
  sections: SidebarSection[];
}

export interface NavItems {
  isReady: boolean;
  items: NavItem[];
  showSettings: boolean;
  payrollSettings: PayrollSettingsAccessPolicy;
}

export const useNavItems = (): NavItems => {
  const { pathname, search } = useLocation();
  const { data: userInfo, isLoading: isUserLoading } = useGetMyUser();
  const {
    businessId,
    selectedBusiness,
    isLoading: isBusinessLoading,
  } = useBusiness();
  const { canManageUsers } = useUserManagementAccess(businessId);
  const { data: currentBusiness } = useGetBusiness(businessId);
  const { enabled: enablePocketflowsFlag } = useFeatureFlag(
    FEATURE_FLAG_NAMES.enable_pocketflows,
  );
  const { settings } = useBusinessProducts();
  const { canViewRoster } = useCanViewPayrollRoster(selectedBusiness, userInfo);
  const { canManage: canManageAccess } =
    useWorkbookAccessEditorAccess(businessId);
  const payrollSettings = payrollSettingsAccessPolicy({
    canViewRoster,
    canManageAccess,
  });
  const apTabs = useApTabs();
  const jobCostingSections = useJobCostingNav();

  const role = userInfo?.role;
  const capabilities = getProductCapabilities({
    user: userInfo,
    business: currentBusiness ?? selectedBusiness,
    settings,
  });
  const canEditPayrollRules = capabilities.payroll.canEditRules;
  const enableAp = capabilities.enabled.accounts_payable;
  const reviewQueue = useGetReviewQueue(enableAp ? businessId : null);
  const reviewCount = flattenPages(reviewQueue.data?.pages).length;
  const forwardingEmpty = useForwardingEmptyState(
    enableAp ? businessId : null,
    pathname === apListPath(AP_TAB.review) ||
      (pathname === apListPath() &&
        (new URLSearchParams(search).get("tab") ?? AP_TAB.review) ===
          AP_TAB.review),
  );
  const enableReferrals = capabilities.enabled.referrals;
  const visibility: NavVisibility = {
    enablePocketflows:
      (selectedBusiness?.is_pocketflows_enabled && enablePocketflowsFlag) ??
      false,
    hasPayBreakdownAccess: capabilities.payroll.hasOutput,
    canEditPayrollRules,
    enableReferrals,
    enableAp,
    canSeePayrollPreview: capabilities.preview.payroll,
    canSeeApPreview: capabilities.preview.accounts_payable,
  };

  const requestedApTab = apActiveTab(pathname, search);
  const activeApTab =
    !requestedApTab || apTabs.some(({ tab }) => tab === requestedApTab)
      ? requestedApTab
      : AP_TAB.review;
  const apSections: SidebarSection[] =
    enableAp && !forwardingEmpty
      ? apTabs.map(({ tab, warning }) => ({
          label: AP_TAB_LABEL[tab],
          to: apListPath(tab, search),
          icon: apTabIcon(tab),
          isActive: tab === activeApTab,
          warning,
          ...(tab === AP_TAB.review && reviewCount > 0
            ? {
                count: reviewCount,
                countIsPartial: !!reviewQueue.hasNextPage,
                countLabel: "to review",
              }
            : {}),
        }))
      : [];

  const payrollTarget = () => {
    const [page, periodStart] = pathname.split("/").filter(Boolean);
    return page === "payroll-details" &&
      periodStart &&
      periodStart !== "statements"
      ? getPayBreakdownPath(periodStart)
      : PAY_BREAKDOWN_PATH;
  };

  const items = NAV_ITEM_DEFS.filter(
    (item) =>
      (!isApOnly(role) ||
        item.href === NAV_ROUTES.accountsPayable ||
        item.href === NAV_ROUTES.payrollRules) &&
      (item.show?.(visibility) ?? true),
  ).map((item) => ({
    ...item,
    isActive: isPathUnder(pathname, item.href),
    to: item.href === PAY_BREAKDOWN_PATH ? payrollTarget() : item.href,
    sections:
      item.href === NAV_ROUTES.accountsPayable
        ? apSections
        : item.href === NAV_ROUTES.profitability
          ? jobCostingSections
          : [],
  }));

  const canSeeBusinessSettings =
    enableReferrals && !isTech(role) && !!businessId;

  return {
    isReady: !!userInfo && !isUserLoading && !isBusinessLoading,
    items: isTech(role) ? [] : foldNavPanels(items),
    showSettings:
      !isApOnly(role) &&
      !isTech(role) &&
      (payrollSettings.pages.length > 0 ||
        canManageUsers ||
        (canSeeBusinessSettings && !canEditPayrollRules)),
    payrollSettings,
  };
};
